import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import { Buffer } from 'buffer';
import bill from '../assets/bill.jpg';
import stamp from '../assets/stamp.jpg';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}[character]));

const formatAmount = (value) => (Number(value) || 0).toFixed(2);

const formatDate = (value) => value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB')
  : '';

export const buildVatTrialPrintHtml = (trial) => {
  const seller = trial.seller || {};
  const client = trial.client || {};
  const invoice = trial.invoice || {};
  const products = trial.products || [];
  const qrValue = JSON.stringify({
    documentType: 'VAT Trial',
    invoiceNumber: trial.invoiceNumber,
    seller,
    client,
    invoice,
    natureOfGoodsInWords: products[0]?.natureOfGoods || '',
    amountInWords: trial.productDescription || '',
    products,
    totals: trial.totals,
    bankDetails: {
      accountName: 'LAR Contracting Company',
      bank: 'Al Rajhi Bank',
      account: '51300-001-0006085423395',
      iban: 'SA5380000513608015423395'
    }
  });

  if (Buffer.byteLength(qrValue, 'utf8') > 2800) {
    throw new Error('The VAT Trial details exceed QR code capacity. Shorten the details before printing.');
  }

  const qrMarkup = renderToStaticMarkup(<QRCodeSVG value={qrValue} size={150} level="L" />);
  const productRows = products.map((product, index) => `
    <tr>
      <td class="center">${formatAmount(product.totalInclVAT)}</td>
      <td class="center">${formatAmount(product.vatAmount)}</td>
      <td class="center">${escapeHtml(product.vatRate)}%</td>
      <td class="center">${formatAmount(product.totalExclVAT)}</td>
      <td class="center">${formatAmount(product.unitRate)}</td>
      <td class="center">${escapeHtml(product.quantity)}</td>
      <td class="center">${escapeHtml(product.unit)}</td>
      <td class="goods">${escapeHtml(product.natureOfGoods)}<br><span class="arabic" dir="rtl">${escapeHtml(product.natureOfGoods)}</span></td>
      <td class="center">${index + 1}</td>
    </tr>
  `).join('');
  const totals = trial.totals || {};

  return `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>VAT Trial ${escapeHtml(trial.invoiceNumber)}</title>
        <style>
          * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { margin: 0; padding: 12px; color: #111; font: 10px Arial, sans-serif; }
          table { width: 100%; max-width: 900px; margin: 8px auto 0; border-collapse: collapse; table-layout: fixed; }
          th, td { border: 1px solid #d8d2cc; padding: 4px; height: 20px; line-height: 1.1; overflow-wrap: anywhere; }
          th { background: #f3f0eb; text-align: left; }
          .header { display: flex; align-items: center; justify-content: center; gap: 20px; }
          .header img { max-width: 100px; height: auto; }
          .header h1 { margin: 0; color: #1D2A5F; font-size: 22px; }
          .header h2 { margin: 5px 0 0; color: #D55C36; font-size: 14px; }
          .title { margin: 8px 0; padding: 7px 0; color: #1D2A5F; text-align: center; font-size: 17px; }
          .number-row { display: flex; justify-content: space-between; color: #D55C36; font-size: 12px; }
          .section-title td, .bank-title { color: white; background: #1D2A5F; font-weight: bold; }
          .section-title h3 { margin: 0; font-size: 12px; }
          .section-title .right { float: right; direction: rtl; }
          .section-title .left { float: left; }
          .clearfix::after { content: ""; display: table; clear: both; }
          .col2 { width: 12.5%; }
          .align-end, .arabic { text-align: right; direction: rtl; }
          .date-table th, .date-table td, .center { text-align: center; }
          .date-table th { height: 42px; }
          .products th { height: 48px; text-align: center; vertical-align: middle; }
          .products td { text-align: center; }
          .products .goods { width: 31%; text-align: left; }
          .products th:not(.goods), .products td:not(.goods) { width: 8%; }
          .arabic { font-family: Arial, sans-serif; }
          .summary .amount { width: 16%; text-align: center; }
          .summary .label-ar { width: 30%; text-align: right; direction: rtl; }
          .summary .words { width: 38%; text-align: center; vertical-align: middle; }
          .signature { height: 90px; text-align: center; vertical-align: bottom; }
          .qr { width: 20%; text-align: center; vertical-align: middle; }
          .qr svg { width: 120px; height: 120px; }
          .bank-title { text-align: center; }
          @media print { body { padding: 0; } table { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div style="text-align:right"><h1>LAR Contracting Company</h1></div>
          <img src="${bill}" alt="LAR Contracting Company logo">
        </div>
        <h2 class="title">VAT Trial / فاتورة ضريبة القيمة المضافة</h2>
        <div class="number-row"><strong>Invoice No: ${escapeHtml(trial.invoiceNumber)}</strong><strong dir="rtl">رقم الفاتورة: ${escapeHtml(trial.invoiceNumber)}</strong></div>

        <table class="detail">
          <tr class="section-title"><td colspan="8"><div class="clearfix"><h3 class="left">Our Details</h3><h3 class="right">تفاصيلنا</h3></div></td></tr>
          <tr><th class="col2">Name:</th><td colspan="3">LAR Contracting Company</td><td colspan="3" class="arabic">شركة إل آر كونتراكتنج</td><th class="align-end">الاسم:</th></tr>
          <tr><th>Short Address:</th><td colspan="3">${escapeHtml(seller.shortAddress || 'ENAA8599')}</td><td colspan="3" class="arabic">${escapeHtml(seller.shortAddress || 'ENAA8599')}</td><th class="align-end">العنوان المختصر:</th></tr>
          <tr><th>Secondary No:</th><td colspan="3">${escapeHtml(seller.secondaryNo || '2287')}</td><td colspan="3" class="arabic">${escapeHtml(seller.secondaryNo || '2287')}</td><th class="align-end">الرقم الثانوي:</th></tr>
          <tr><th>Building No:</th><td>${escapeHtml(seller.buildingNo || '8599')}</td><th>City:</th><td>${escapeHtml(seller.city || '')}</td><td class="arabic">فرع الأمير سعود بن عبدالله بن جلوي، الجبيل</td><th class="align-end">المدينة:</th><td class="arabic">${escapeHtml(seller.buildingNo || '8599')}</td><th class="align-end">رقم المبنى:</th></tr>
          <tr><th>Customer Account:</th><td>${escapeHtml(seller.addlNo || '31335115107')}</td><th>District:</th><td>${escapeHtml(seller.district || '')}</td><td class="arabic">حي طيبة</td><th class="align-end">الحي:</th><td class="arabic">${escapeHtml(seller.addlNo || '31335115107')}</td><th class="align-end">رقم حساب العميل:</th></tr>
          <tr><th>Postal Code:</th><td>${escapeHtml(seller.postalCode || '35513')}</td><th>Country:</th><td>${escapeHtml(seller.country || 'Kingdom of Saudi Arabia')}</td><td class="arabic">المملكة العربية السعودية</td><th class="align-end">الدولة:</th><td class="arabic">${escapeHtml(seller.postalCode || '35513')}</td><th class="align-end">الرمز البريدي:</th></tr>
          <tr><th>VAT Number:</th><td>${escapeHtml(seller.vatNumber || '314702232800003')}</td><th>CRN:</th><td colspan="2" class="center">${escapeHtml(seller.crn || '7053932963')}</td><th class="align-end">السجل التجاري:</th><td class="arabic">${escapeHtml(seller.vatNumber || '314702232800003')}</td><th class="align-end">رقم ضريبة القيمة المضافة:</th></tr>
        </table>

        <table class="detail">
          <tr class="section-title"><td colspan="8"><div class="clearfix"><h3 class="left">Client Details</h3><h3 class="right">تفاصيل العميل</h3></div></td></tr>
          <tr><th class="col2">Name:</th><td colspan="3">${escapeHtml(client.clientName)}</td><td colspan="3" class="arabic">${escapeHtml(client.clientName)}</td><th class="align-end">الاسم:</th></tr>
          <tr><th>Address:</th><td colspan="3">${escapeHtml(client.clientAddress)}</td><td colspan="3" class="arabic">${escapeHtml(client.clientAddress)}</td><th class="align-end">العنوان:</th></tr>
          <tr><th>Addl. No:</th><td>${escapeHtml(client.clientAddNo)}</td><th>District:</th><td>${escapeHtml(client.clientDistrict)}</td><td class="arabic">${escapeHtml(client.clientDistrict)}</td><th class="align-end">الحي:</th><td class="arabic">${escapeHtml(client.clientAddNo)}</td><th class="align-end">الرقم الإضافي:</th></tr>
          <tr><th>Postal Code:</th><td>${escapeHtml(client.clientPostalCode)}</td><th>Country:</th><td>${escapeHtml(client.clientCountry)}</td><td class="arabic">${escapeHtml(client.clientCountry)}</td><th class="align-end">الدولة:</th><td class="arabic">${escapeHtml(client.clientPostalCode)}</td><th class="align-end">الرمز البريدي:</th></tr>
          <tr><th>VAT Number:</th><td>${escapeHtml(client.clientVatNumber)}</td><th>CRN:</th><td colspan="2" class="center">${escapeHtml(client.clientCRN)}</td><th class="align-end">السجل التجاري:</th><td class="arabic">${escapeHtml(client.clientVatNumber)}</td><th class="align-end">رقم ضريبة القيمة المضافة:</th></tr>
        </table>

        <table class="date-table">
          <tr><th>تاريخ الفاتورة<br>Invoice Date</th><th>تاريخ التوريد<br>Supply Date</th><th>رقم العقد / أمر الشراء<br>Contract/PO No</th><th>فترة الفاتورة<br>Invoice Period</th><th>رقم المشروع / المرجع<br>Project/Reference No</th></tr>
          <tr><td>${formatDate(invoice.invoiceDate)}</td><td>${formatDate(invoice.supplyDate)}</td><td>${escapeHtml(invoice.contractNo)}</td><td>${escapeHtml(invoice.invoicePeriod)}</td><td>${escapeHtml(invoice.projectRefNo)}</td></tr>
        </table>

        <table class="products">
          <thead><tr>
            <th>Total Incl. VAT (SAR)<br>الإجمالي شامل الضريبة</th><th>VAT Amount (SAR)<br>مبلغ ضريبة القيمة المضافة</th><th>VAT %<br>نسبة الضريبة</th><th>Total Excl. VAT (SAR)<br>الإجمالي بدون ضريبة</th><th>Unit Rate (SAR)<br>سعر الوحدة</th><th>Qty / Hours<br>الكمية</th><th>Unit<br>الوحدة</th><th class="goods">Nature of Goods/Services<br>طبيعة السلع/الخدمات</th><th>#</th>
          </tr></thead>
          <tbody>${productRows}</tbody>
        </table>

        <table class="summary">
          <tr><td>Total Excl. VAT (SAR):</td><td class="amount">${formatAmount(totals.totalExclVAT)}</td><td class="label-ar">الإجمالي بدون ضريبة (ريال سعودي)</td><td colspan="4" rowspan="2" class="words">${escapeHtml(trial.productDescription)}</td></tr>
          <tr><td>VAT Amount (SAR):</td><td class="amount">${formatAmount(totals.totalVAT)}</td><td class="label-ar">مبلغ ضريبة القيمة المضافة (ريال سعودي)</td></tr>
          <tr><td>Total Incl. VAT (SAR):</td><td class="amount">${formatAmount(totals.totalInclVAT)}</td><td class="label-ar">الإجمالي شامل الضريبة (ريال سعودي)</td><td colspan="3" rowspan="2" class="signature">&nbsp;</td></tr>
          <tr><td>Balance Due (SAR):</td><td class="amount">${formatAmount(totals.balanceDue)}</td><td class="label-ar">الرصيد المستحق (ر.س)</td></tr>
        </table>

        <table>
          <tr><th colspan="4" class="bank-title">Bank Details / التفاصيل المصرفية</th><th colspan="2" class="bank-title">Prepared By / معد الفاتورة</th><th colspan="2" class="bank-title">Received By / مستلم الفاتورة</th><td rowspan="6" class="qr">${qrMarkup}</td></tr>
          <tr><td colspan="2"><strong>ACCOUNT NAME</strong> / اسم الحساب</td><td colspan="2">LAR Contracting Company<br>شركة إل آر كونتراكتنج</td><td colspan="2" rowspan="5" class="signature" style="height:140px;padding:0;"><img src="${stamp}" alt="Signature with stamp" style="width:100%;height:140px;object-fit:fill;display:block;" /></td><td colspan="3" rowspan="5" class="signature">&nbsp;</td></tr>
          <tr><td colspan="2"><strong>BANK</strong> / بنك</td><td colspan="2">Al Rajhi Bank</td></tr>
          <tr><td colspan="2"><strong>ACCOUNT</strong> / الحساب</td><td colspan="2">51300-001-0006085423395</td></tr>
          <tr><td colspan="2"><strong>IBAN</strong> / ايبان</td><td colspan="2">SA5380000513608015423395</td></tr>
          <tr><td colspan="2"></td><td colspan="2"></td></tr>
        </table>
      </body>
    </html>`;
};
